/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
  IonLabel,
  IonItem,
  IonList,
  IonToggle,
  useIonRouter,
  isPlatform,
  IonModal,
  IonButton,
  IonIcon,
} from "@ionic/react";
import Header from "../../components/Header";
import { Preferences } from "@capacitor/preferences";
import { getUserPreferences, updateUserPreferences } from "../../services/user";
import {
  AvailableHealthServices,
  requestHealthKitPermissions,
} from "../../services/healthkit";
import { useStorage } from "../../contexts/StorageContext";
import { closeOutline, informationCircleOutline } from "ionicons/icons";
import { detectDeviceType } from "../../utils/helpers";
import {
  TRACK_OPTIONS_ANDROID,
  TRACK_OPTIONS_IOS,
} from "../../utils/constants";
import { App } from "@capacitor/app";
import { Health as CapGoHealth } from "@capgo/capacitor-health";
import useAuth from "../../hooks/useAuth";

// Health data type constants (replacing old SampleNames enum)
const HEALTH_DATA_TYPES = {
  SLEEP_ANALYSIS: "sleep",
  HEART_RATE: "heartRate",
  STEPS: "steps",
} as const;

const LabelStyle: React.CSSProperties = {
  fontFamily: "'Figtree'",
  fontStyle: "normal",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "16px",
  textTransform: "uppercase",
  color: "#586670",
};

const itemLabelStyle: React.CSSProperties = {
  fontFamily: "'Figtree'",
  fontStyle: "normal",
  fontWeight: 500,
  fontSize: "18px",
  lineHeight: "22px",
  fontFeatureSettings: "'case' on",
  color: "#586670",
};
export class HealthPreferencesStorage {
  private static readonly STORAGE_KEY_PREFIX = "health_preferences";
  static async loadUserPreferencesFromService(
    deviceType = "apple"
  ): Promise<string[]> {
    const prefsPayload = {
      health_connect: true,
      track_health_data: {
        apple: deviceType === "apple",
        google: deviceType === "google",
        step_tracking: true,
        sleep_tracking: true,
        heart_rate: true,
      },
    };

    const response = await getUserPreferences(prefsPayload);
    const trackHealthData = response.data?.track_health_data;
    console.log("Loaded preferences from service:", trackHealthData);

    if (!trackHealthData) {
      return [];
    }

    const selectedTracks: string[] = [];
    if (deviceType === "apple") {
      if (trackHealthData.step_tracking) selectedTracks.push("stepCount");
      if (trackHealthData.sleep_tracking)
        selectedTracks.push(HEALTH_DATA_TYPES.SLEEP_ANALYSIS);
      if (trackHealthData.heart_rate)
        selectedTracks.push(HEALTH_DATA_TYPES.HEART_RATE);
    } else if (deviceType === "google") {
      if (trackHealthData.step_tracking) selectedTracks.push("Steps");
      if (trackHealthData.heart_rate) selectedTracks.push("HeartRateSeries");
    }

    await this.savePreferences(deviceType, selectedTracks);
    console.log(
      `Preferences loaded from service for ${deviceType}:`,
      selectedTracks
    );
    return selectedTracks;
  }

  static async savePreferences(deviceType: string, selectedData: string[]) {
    try {
      const key = deviceType;
      await Preferences.set({
        key,
        value: JSON.stringify(selectedData),
      });
      console.log(`Preferences saved for ${deviceType}:`, selectedData);
    } catch (error) {
      console.error("Error saving preferences:", error);
      throw error;
    }
  }

  static async getPreferences(deviceType: string): Promise<string[]> {
    try {
      const key = deviceType;
      const result = await Preferences.get({ key });
      if (result.value) {
        try {
          const preferences = JSON.parse(result.value);
          return Array.isArray(preferences) ? preferences : [];
        } catch (err) {
          console.warn("Corrupted preferences JSON, clearing it.", err);
          await Preferences.remove({ key });
          return [];
        }
      }
      return [];
    } catch (error) {
      console.error("Error loading preferences:", error);
      return [];
    }
  }

  static async clearPreferences(deviceType: string) {
    try {
      const key = deviceType;
      await Preferences.remove({ key });
    } catch (error) {
      console.error("Error clearing preferences:", error);
      throw error;
    }
  }

  static async getAllStoredKeys(): Promise<string[]> {
    try {
      const result = await Preferences.keys();
      return result.keys.filter((key) =>
        key.startsWith(this.STORAGE_KEY_PREFIX)
      );
    } catch (error) {
      console.error("Error getting stored keys:", error);
      return [];
    }
  }
}

const MyHealthAccess: React.FC<any> = () => {
  const { platFormType } = useAuth();
  const [selectedData, setSelectedData] = useState<string[]>([]);
  const [deviceType, setDeviceType] = useState<string>("apple");
  const [isHealthKitEnabled, setIsHealthKitEnabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [availableServices, setAvailableServices] = useState<string[]>([]);
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);

  const platform = platFormType;
  const platformName =
    platform === "ios"
      ? "Apple Health"
      : platform === "android"
      ? "Health Connect"
      : "Health Service";

  const TRACK_OPTIONS =
    platform === "ios" ? TRACK_OPTIONS_IOS : TRACK_OPTIONS_ANDROID;

  const { storage } = useStorage();
  const router = useIonRouter();

  const handleBack = () => {
    router.push("/settings");
  };

  useEffect(() => {
    if (!storage) return;

    const initialize = async () => {
      setIsLoading(true);
      try {
        const healthKitStatus = await Preferences.get({
          key: "accessHealthKit",
        });
        const isEnabled = healthKitStatus.value === "true";
        const type = detectDeviceType(isEnabled);

        setDeviceType(type);
        setIsHealthKitEnabled(isEnabled);
        await fetchSelectedTracksInBackground(type);

        const savedPreferences = await HealthPreferencesStorage.getPreferences(
          type
        );
        setSelectedData(
          Array.isArray(savedPreferences) ? savedPreferences : []
        );

        setIsInitialized(true);
      } catch (error) {
        console.error("Initialization error:", error);
        setSelectedData([]);
        setIsInitialized(true);
      } finally {
        setIsLoading(false);
      }
    };

    initialize();
  }, [storage]);

  useEffect(() => {
    const fetchAvailableServices = async () => {
      const services = await AvailableHealthServices();
      setAvailableServices(services);
    };
    fetchAvailableServices();
  }, []);

  const fetchSelectedTracksInBackground = async (type: string) => {
    try {
      const prefsPayload = {
        health_connect: true,
        track_health_data: {
          apple: type === "apple",
          google: type === "google",
          step_tracking: true,
          sleep_tracking: true,
          heart_rate: true,
        },
      };
      const response = await getUserPreferences(prefsPayload);
      console.log("Background sync response:", JSON.stringify(response));
      const trackHealthData = response.data?.track_health_data;
      if (!trackHealthData) return;

      const selectedTracks: string[] = [];
      if (platform === "ios") {
        console.log("Track health data (iOS):", trackHealthData);
        if (trackHealthData.step_tracking) selectedTracks.push("stepCount");
        if (trackHealthData.sleep_tracking)
          selectedTracks.push(HEALTH_DATA_TYPES.SLEEP_ANALYSIS);
        if (trackHealthData.heart_rate)
          selectedTracks.push(HEALTH_DATA_TYPES.HEART_RATE);
      } else if (platform === "android") {
        console.log("Track health data (Android):", trackHealthData);
        if (trackHealthData.step_tracking) selectedTracks.push("Steps");
        if (trackHealthData.heart_rate) selectedTracks.push("HeartRateSeries");
      }

      await HealthPreferencesStorage.savePreferences(type, selectedTracks);
    } catch (error) {
      console.error("Background sync error:", error);
    }
  };

  const syncWithAPI = async (
    healthKitEnabled: boolean,
    selectedTracking: string[]
  ) => {
    if (isSyncing) return;

    setIsSyncing(true);
    try {
      const track_health_data =
        platform === "ios"
          ? {
              apple: true,
              google: false,
              step_tracking:
                healthKitEnabled && selectedTracking.includes("stepCount"),
              sleep_tracking:
                healthKitEnabled &&
                selectedTracking.includes(HEALTH_DATA_TYPES.SLEEP_ANALYSIS),
              heart_rate:
                healthKitEnabled &&
                selectedTracking.includes(HEALTH_DATA_TYPES.HEART_RATE),
            }
          : {
              apple: false,
              google: true,
              step_tracking:
                healthKitEnabled && selectedTracking.includes("Steps"),
              heart_rate:
                healthKitEnabled &&
                selectedTracking.includes("HeartRateSeries"),
            };

      const prefsPayload = {
        health_connect: healthKitEnabled,
        has_biometric: false,
        other_treatments: {},
        track_health_data,
      };

      await HealthPreferencesStorage.savePreferences(
        deviceType,
        selectedTracking
      );

      updateUserPreferences(prefsPayload).catch((err) =>
        console.error("API update failed:", err)
      );

      if (healthKitEnabled && selectedTracking.length > 0) {
        if (
          (platform === "ios" && availableServices.includes("HealthKit")) ||
          (platform === "android" &&
            availableServices.includes("Health Connect"))
        ) {
          try {
            const result = await requestHealthKitPermissions(selectedTracking);
            // Ensure the preference is set after successful permission grant
            if (result.success) {
              await Preferences.set({ key: "accessHealthKit", value: "true" });
            }
          } catch (err) {
            console.error("Permission request failed:", err);
          }
        }
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleHealthKitToggle = async (checked: boolean) => {
    try {
      const allTracks = TRACK_OPTIONS.map((opt) => opt.key);
      if (checked) {
        setSelectedData(allTracks);
      }
      setIsHealthKitEnabled(checked);
      await Preferences.set({
        key: "accessHealthKit",
        value: checked ? "true" : "false",
      });

      if (!checked) {
        setSelectedData([]);
        await HealthPreferencesStorage.clearPreferences(deviceType);
        await syncWithAPI(false, []);
        return;
      }

      if (
        (platform === "ios" && !availableServices.includes("HealthKit")) ||
        (platform === "android" &&
          !availableServices.includes("Health Connect"))
      ) {
        setPopupOpen(true);
      }
      await syncWithAPI(true, allTracks);
    } catch (error) {
      console.error("Toggle error:", error);
    }
  };

  const handleDataSelection = async (value: string, checked: boolean) => {
    const newSelected = checked
      ? [...selectedData, value]
      : selectedData.filter((item) => item !== value);

    const shouldEnable = checked && !isHealthKitEnabled;

    setSelectedData(newSelected);
    if (shouldEnable) {
      setIsHealthKitEnabled(true);
      await Preferences.set({
        key: "accessHealthKit",
        value: "true",
      });
    }
    await syncWithAPI(isHealthKitEnabled || shouldEnable, newSelected);
  };

  if (!storage) {
    return (
      <IonPage>
        <Header
          showGreeting={false}
          showPageTitle={true}
          showUserIcon={false}
          pageTitle="Health App"
          showBackButton={true}
          onBackButtonClick={handleBack}
        />
        <IonContent className="ion-padding">
          <div className="flex justify-center items-center h-full">
            <p>Loading...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header
        showGreeting={false}
        showPageTitle={true}
        showUserIcon={false}
        pageTitle="Health App"
        showBackButton={true}
        onBackButtonClick={handleBack}
      />
      {popupOpen && (
        <IonModal
          isOpen={popupOpen}
          onDidDismiss={() => setPopupOpen(false)}
          canDismiss={true}
          showBackdrop={true}
          backdropDismiss={true}
          style={{
            "--background": "transparent",
            "--box-shadow": "none",
            "--border-radius": "20px",
          }}
        >
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "white",
              borderRadius: "20px",
              maxHeight: "70vh",
              overflow: "hidden",
              margin: "20px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                margin: "12px",
              }}
            >
              <div style={{ width: "40px" }}></div>
              <h2
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  margin: "0",
                  color: "#000",
                  flex: 1,
                  textAlign: "center",
                }}
              >
                Connect to {platformName}
              </h2>
              <br />
              <button
                onClick={() => setPopupOpen(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#999",
                  cursor: "pointer",
                  padding: "8px",
                  width: "40px",
                  height: "40px",
                  display: "flex",
                  alignItems: "top",
                  justifyContent: "top",
                }}
              >
                <IonIcon icon={closeOutline} style={{ fontSize: "24px" }} />
              </button>
            </div>
            <div
              style={{
                alignContent: "center",
                padding: "4px",
                alignItems: "center",
                textAlign: "center",
                marginBottom: "5px",
              }}
            >
              {platformName === "Apple Health"
                ? " Apple Health is not Supported in this Device"
                : `Please download ${platformName} to connect your health data.`}
            </div>
            <div style={{ padding: "0 16px 16px" }}>
              <div
                style={{
                  padding: "0 ",
                  display: "flex",
                  gap: 12,
                  flexDirection: "column",
                }}
              >
                {platformName === "Health Connect" && platform === "android" ? (
                  <IonButton
                    expand="block"
                    fill={"solid"}
                    onClick={() => {
                      window.open(
                        "https://play.google.com/store/apps/details?id=com.google.android.apps.healthdata&pcampaignid=web_share",
                        "_blank"
                      );
                    }}
                    className={"primary-button w-full"}
                    style={{
                      "--border-radius": "30px",
                      fontSize: "18px",
                      fontWeight: "500",
                    }}
                  >
                    Download Health Connect
                  </IonButton>
                ) : null}
                <IonButton
                  expand="block"
                  fill={"outline"}
                  onClick={() => {
                    setPopupOpen(false);
                  }}
                  className={"tertiary-button w-full"}
                  style={{
                    "--border-radius": "30px",
                    fontSize: "18px",
                    fontWeight: "500",
                    "--color": "#ffffff !important",
                    borderRadius: "30px",
                  }}
                >
                  Cancel
                </IonButton>
              </div>
            </div>
          </div>
        </IonModal>
      )}
      <IonContent
        className="ion-content-page !m-2 !p-4"
        style={{ backgroundColor: "#FFF !important" }}
      >
        <div className="section-header mb-4 ml-4 mt-4">
          <div style={LabelStyle}>
            <IonLabel>
              <h2 className="text-gray-500 font-medium">CONNECTED SERVICE</h2>
            </IonLabel>
          </div>
        </div>
        <div className="flex flex-col m-4">
          <div
            className="bg-white rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-6"
            style={itemLabelStyle}
          >
            <IonList lines="none">
              <IonItem>
                <IonLabel style={{ fontSize: "17px" }}>{platformName}</IonLabel>
                <IonToggle
                  slot="end"
                  checked={isHealthKitEnabled}
                  onIonChange={(e) => handleHealthKitToggle(e.detail.checked)}
                  disabled={isLoading}
                />
              </IonItem>
            </IonList>
          </div>
          <div className="section-header mb-4" style={LabelStyle}>
            <IonLabel>
              <h2 className="text-gray-500 font-medium">TRACK DATA</h2>
            </IonLabel>
          </div>
          <div
            className={`rounded-xl overflow-hidden shadow-[0_16px_64px_rgba(0,0,0,0.1)] mb-6`}
          >
            <IonList>
              {TRACK_OPTIONS.map(
                (opt: { key: string; label: string }, idx: number) => (
                  <IonItem
                    key={opt.key}
                    lines={idx === TRACK_OPTIONS.length - 1 ? "none" : "full"}
                  >
                    <IonLabel
                      style={{
                        paddingTop: "9px",
                        height: "39px",
                        ...itemLabelStyle,
                      }}
                    >
                      {opt.label}
                    </IonLabel>
                    <IonToggle
                      slot="end"
                      checked={
                        isHealthKitEnabled && selectedData.includes(opt.key)
                      }
                      onIonChange={(e) =>
                        handleDataSelection(opt.key, e.detail.checked)
                      }
                    />
                  </IonItem>
                )
              )}
            </IonList>
          </div>
        </div>
        {/* System Settings Disclaimer */}
        <div className="fixed bottom-4 left-0 right-0">
          <div className="mx-4 mb-4 bg-gray-200  backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-start space-x-3">
              <IonIcon
                icon={informationCircleOutline}
                className="w-5 h-5 text-gray-800 mt-0.5 flex-shrink-0"
              />
              <div>
                <h3 className="text-black font-medium mb-1">
                  Manage Data Permissions
                </h3>
                <p className="text-gray-800 text-sm mb-2">
                  To change what data is shared, update permissions in your
                  system settings.
                </p>
                {platform === "ios" && (
                  <p className="text-gray-800 mb-2">
                    {
                      "Settings -> Apps -> Health -> Data Access & Devices -> Arc Connect"
                    }
                  </p>
                )}
                {/* show button only for android */}
                {platform === "android" && (
                  <button
                    className="tertiary-button w-full"
                    onClick={async () => {
                      try {
                        await CapGoHealth.openHealthConnectSettings();
                      } catch (error) {
                        console.error("Error opening app settings:", error);
                      }
                    }}
                  >
                    Open System Settings
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

const MyHealthAccessWrapper: React.FC = () => {
  return <MyHealthAccess />;
};

export default MyHealthAccessWrapper;
